import {
  categoryLabel,
  categoryOrder,
  createReferenceCatalog,
  parseReferenceHash,
  referenceHashForEntryId
} from "./catalog.js";
import type {
  AddressingModeEntry,
  AssemblerGuideEntry,
  BasicGuideEntry,
  DebuggerGuideEntry,
  FlagEntry,
  ForthGuideEntry,
  InterruptEntry,
  MemoryRangeEntry,
  OpcodeEntry,
  PublicApiEntry,
  ReferenceCatalog,
  ReferenceEntry,
  ReferenceFilter,
  ReferenceNumberBase,
  ReferenceKind,
  RegisterEntry,
  ShellCommandEntry,
  TrackerGuideEntry
} from "./types.js";

const RECENT_STORAGE_KEY = "am8.reference.recent.v1";
const MAX_RECENT_IDS = 6;

type ReferenceState = {
  activeCategory: ReferenceFilter;
  numberBase: ReferenceNumberBase;
  query: string;
  recentIds: string[];
  selectedId: string | null;
};

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatAddress(value: number, base: ReferenceNumberBase): string {
  return base === "hex" ? `0x${value.toString(16).toUpperCase().padStart(4, "0")}` : value.toString(10);
}

function formatAddressWithAlternate(value: number, base: ReferenceNumberBase): string {
  const primary = formatAddress(value, base);
  const secondary = formatAddress(value, base === "hex" ? "dec" : "hex");
  return `${primary} (${secondary})`;
}

function formatRange(start: number, end: number, base: ReferenceNumberBase): string {
  return `${formatAddress(start, base)}..${formatAddress(end, base)}`;
}

function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase();
}

function loadRecentIds(catalog: ReferenceCatalog): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((value): value is string => typeof value === "string" && catalog.byId.has(value)).slice(0, MAX_RECENT_IDS);
  } catch {
    return [];
  }
}

function saveRecentIds(ids: string[]): void {
  try {
    window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(ids.slice(0, MAX_RECENT_IDS)));
  } catch {
    return;
  }
}

function pushRecentId(recentIds: string[], entryId: string): string[] {
  return [entryId, ...recentIds.filter((id) => id !== entryId)].slice(0, MAX_RECENT_IDS);
}

function searchTerms(entry: ReferenceEntry): string[] {
  const baseTerms = [entry.title, entry.summary, ...entry.aliases, ...entry.keywords, ...entry.relatedIds];
  switch (entry.kind) {
    case "assembler-guide":
      return [...baseTerms, entry.syntax, entry.source, ...entry.examples, ...entry.notes];
    case "basic-guide":
      return [...baseTerms, entry.syntax, entry.source, ...entry.examples, ...entry.notes];
    case "debugger-guide":
      return [...baseTerms, entry.syntax, entry.source, ...entry.examples, ...entry.notes];
    case "tracker-guide":
      return [...baseTerms, entry.syntax, entry.source, ...entry.examples, ...entry.notes];
    case "forth-guide":
      return [...baseTerms, entry.syntax, entry.source, ...entry.examples, ...entry.notes];
    case "shell-command":
      return [...baseTerms, entry.syntax, entry.availability, ...entry.examples, ...entry.notes];
    case "opcode":
      return [
        ...baseTerms,
        ...entry.affectedFlags,
        ...entry.examples,
        ...entry.variants.flatMap((variant) => [variant.opcode, variant.modeLabel, variant.flagsText, variant.cycles])
      ];
    case "addressing-mode":
      return [...baseTerms, entry.syntax, entry.effectiveAddress, entry.valueKind, ...entry.notes];
    case "register":
      return [
        ...baseTerms,
        entry.addressLabel ?? "",
        entry.access,
        entry.reset,
        entry.scope,
        entry.width,
        ...entry.notes,
        ...entry.bitFields.flatMap((field) => [field.label, field.value, field.description])
      ];
    case "flag":
      return [...baseTerms, entry.bit, entry.meaning, ...entry.setBy, ...entry.clearedBy];
    case "memory-range":
      return [...baseTerms, entry.owner, entry.access, ...entry.notes];
    case "public-api":
      return [
        ...baseTerms,
        entry.addressLabel ?? "",
        entry.address === null ? "" : `0x${entry.address.toString(16).toUpperCase()}`,
        entry.address === null ? "" : `$${entry.address.toString(16).toUpperCase()}`,
        entry.binding,
        entry.scope,
        ...entry.inputs,
        ...entry.outputs,
        ...entry.notes
      ];
    case "interrupt":
      return [
        ...baseTerms,
        entry.response,
        ...entry.sources,
        ...entry.acknowledge,
        ...entry.notes,
        ...entry.vectors.flatMap((vector) => [vector.label, `0x${vector.address.toString(16).toUpperCase()}`, `$${vector.address.toString(16).toUpperCase()}`, vector.address.toString(10)])
      ];
  }
}

function scoreEntry(entry: ReferenceEntry, query: string): number | null {
  const normalized = normalizeQuery(query);
  if (normalized === "") {
    return 0;
  }
  const tokens = normalized.split(/\s+/).filter((token) => token !== "");
  if (tokens.length === 0) {
    return 0;
  }

  const title = entry.title.toLowerCase();
  const aliases = entry.aliases.map((alias) => alias.toLowerCase());
  const keywords = entry.keywords.map((keyword) => keyword.toLowerCase());
  const haystack = searchTerms(entry).join(" ").toLowerCase();
  let score = 0;
  for (const token of tokens) {
    if (title === token || aliases.includes(token)) {
      score += 160;
      continue;
    }
    if (title.startsWith(token) || aliases.some((alias) => alias.startsWith(token))) {
      score += 110;
      continue;
    }
    if (title.includes(token) || aliases.some((alias) => alias.includes(token))) {
      score += 75;
      continue;
    }
    if (keywords.some((keyword) => keyword.includes(token))) {
      score += 45;
      continue;
    }
    if (haystack.includes(token)) {
      score += 20;
      continue;
    }
    return null;
  }
  return score;
}

function compareEntries(left: ReferenceEntry, right: ReferenceEntry): number {
  if (left.kind === "public-api" && right.kind === "public-api") {
    return (left.address ?? Number.MAX_SAFE_INTEGER) - (right.address ?? Number.MAX_SAFE_INTEGER) || left.title.localeCompare(right.title);
  }
  if (left.kind === "memory-range" && right.kind === "memory-range") {
    return left.start - right.start;
  }
  if (left.kind === "flag" && right.kind === "flag") {
    return right.bit.localeCompare(left.bit);
  }
  return left.title.localeCompare(right.title);
}

function matchesCategory(entry: ReferenceEntry, category: ReferenceFilter): boolean {
  return category === "all" || entry.kind === category;
}

function isOverview(state: ReferenceState): boolean {
  return state.activeCategory === "all" && normalizeQuery(state.query) === "";
}

function visibleEntries(catalog: ReferenceCatalog, state: ReferenceState): ReferenceEntry[] {
  const filtered = catalog.entries.filter((entry) => matchesCategory(entry, state.activeCategory));
  const query = normalizeQuery(state.query);
  if (query === "") {
    return filtered.slice().sort(compareEntries);
  }
  return filtered
    .map((entry) => ({ entry, score: scoreEntry(entry, query) }))
    .filter((item): item is { entry: ReferenceEntry; score: number } => item.score !== null)
    .sort((left, right) => right.score - left.score || compareEntries(left.entry, right.entry))
    .map((item) => item.entry);
}

function syncSelection(catalog: ReferenceCatalog, state: ReferenceState): void {
  if (isOverview(state)) {
    return;
  }
  const entries = visibleEntries(catalog, state);
  if (entries.length === 0) {
    state.selectedId = null;
    return;
  }
  if (state.selectedId === null || !entries.some((entry) => entry.id === state.selectedId)) {
    state.selectedId = entries[0].id;
  }
}

function updateLocationHash(entryId: string | null): void {
  const url = new URL(window.location.href);
  url.hash = referenceHashForEntryId(entryId);
  window.history.replaceState(null, "", url);
}

function renderChipButtons(
  catalog: ReferenceCatalog,
  entryIds: string[],
  selectedId: string | null,
  className: string,
  clearQuery = true
): string {
  return entryIds
    .map((entryId) => catalog.byId.get(entryId))
    .filter((entry): entry is ReferenceEntry => entry !== undefined)
    .map((entry) => {
      const selectedClass = entry.id === selectedId ? " is-selected" : "";
      return `<button class="${className}${selectedClass}" type="button" data-reference-action="open-entry" data-reference-entry-id="${escapeHtml(entry.id)}" data-reference-category="${escapeHtml(entry.kind)}" data-reference-clear-query="${clearQuery ? "true" : "false"}">${escapeHtml(entry.title)}</button>`;
    })
    .join("");
}

function entryMeta(entry: ReferenceEntry, base: ReferenceNumberBase): string {
  switch (entry.kind) {
    case "assembler-guide":
      return `${entry.source} | ${entry.syntax}`;
    case "basic-guide":
      return `${entry.source} | ${entry.syntax}`;
    case "debugger-guide":
      return `${entry.source} | ${entry.syntax}`;
    case "tracker-guide":
      return `${entry.source} | ${entry.syntax}`;
    case "forth-guide":
      return `${entry.source} | ${entry.syntax}`;
    case "shell-command":
      return `${entry.availability} | ${entry.syntax}`;
    case "opcode":
      return `${entry.variants.length} variants${entry.affectedFlags.length > 0 ? ` | flags ${entry.affectedFlags.join(", ")}` : " | flags unchanged"}`;
    case "addressing-mode":
      return `${entry.bytes} byte${entry.bytes === 1 ? "" : "s"} | ${entry.valueKind}`;
    case "register":
      return `${entry.scope}${entry.address === null ? "" : ` | ${formatAddressWithAlternate(entry.address, base)}`}`;
    case "flag":
      return `${entry.bit} | status register`;
    case "memory-range":
      return `${formatRange(entry.start, entry.end, base)} | ${entry.owner}`;
    case "public-api":
      return `${entry.scope}${entry.address === null ? "" : ` | ${formatAddressWithAlternate(entry.address, base)}`}`;
    case "interrupt":
      return `${entry.vectors.map((vector) => formatAddress(vector.address, base)).join(" | ")} | interrupt flow`;
  }
}

function renderSidebar(catalog: ReferenceCatalog, state: ReferenceState): string {
  const categoryButtons = categoryOrder()
    .map((kind) => {
      const active = state.activeCategory === kind ? " is-active" : "";
      return `<button class="reference-nav__button${active}" type="button" data-reference-action="set-category" data-reference-category="${kind}" aria-pressed="${state.activeCategory === kind ? "true" : "false"}"><span>${escapeHtml(categoryLabel(kind))}</span><span>${catalog.counts[kind]}</span></button>`;
    })
    .join("");
  const recentMarkup =
    state.recentIds.length === 0
      ? '<p class="reference-sidebar__empty">Nothing viewed yet. Search for `ADC`, `PC`, or `$D012`.</p>'
      : renderChipButtons(catalog, state.recentIds, state.selectedId, "reference-link-chip");

  return `
    <div class="reference-sidebar__section">
      <p class="reference-sidebar__label">Overview</p>
      <button class="reference-nav__button${state.activeCategory === "all" ? " is-active" : ""}" type="button" data-reference-action="set-category" data-reference-category="all" aria-pressed="${state.activeCategory === "all" ? "true" : "false"}"><span>All Topics</span><span>${catalog.entries.length}</span></button>
      <div class="reference-nav">${categoryButtons}</div>
    </div>
    <div class="reference-sidebar__section">
      <p class="reference-sidebar__label">Recent</p>
      <div class="reference-chip-row">${recentMarkup}</div>
    </div>
  `;
}

function renderQuickEntries(catalog: ReferenceCatalog): string {
  return catalog.quickEntries
    .map(
      (entry) => `
        <button class="reference-card reference-card--quick" type="button" data-reference-action="open-entry" data-reference-entry-id="${escapeHtml(entry.entryId)}" data-reference-category="${escapeHtml(entry.category)}" data-reference-clear-query="true">
          <span class="reference-card__eyebrow">${escapeHtml(categoryLabel(entry.category))}</span>
          <span class="reference-card__title">${escapeHtml(entry.title)}</span>
          <span class="reference-card__meta">${catalog.counts[entry.category]} entries</span>
          <span class="reference-card__body">${escapeHtml(entry.description)}</span>
        </button>
      `
    )
    .join("");
}

function renderResultButton(entry: ReferenceEntry, state: ReferenceState): string {
  const selected = entry.id === state.selectedId ? " is-selected" : "";
  return `
    <button class="reference-result${selected}" type="button" data-reference-action="open-entry" data-reference-entry-id="${escapeHtml(entry.id)}">
      <span class="reference-result__top">
        <span class="reference-result__title">${escapeHtml(entry.title)}</span>
        <span class="reference-result__kind">${escapeHtml(categoryLabel(entry.kind))}</span>
      </span>
      <span class="reference-result__summary">${escapeHtml(entry.summary)}</span>
      <span class="reference-result__meta">${escapeHtml(entryMeta(entry, state.numberBase))}</span>
    </button>
  `;
}

function renderResults(catalog: ReferenceCatalog, state: ReferenceState): string {
  if (isOverview(state)) {
    return `
      <section class="reference-overview-block">
        <div class="reference-section-heading">
          <h3>Quick Entry</h3>
          <p>Jump straight into the most common machine-reference slices.</p>
        </div>
        <div class="reference-card-grid">${renderQuickEntries(catalog)}</div>
      </section>
      <section class="reference-overview-block">
        <div class="reference-section-heading">
          <h3>Common Lookups</h3>
          <p>Frequent jumps for mnemonics, flags, and key machine addresses.</p>
        </div>
        <div class="reference-chip-row">${renderChipButtons(catalog, catalog.commonLookupIds, state.selectedId, "reference-link-chip")}</div>
      </section>
      <section class="reference-overview-block">
        <div class="reference-section-heading">
          <h3>Highlighted References</h3>
          <p>A compact cross-section of language, machine, and ROM references.</p>
        </div>
        <div class="reference-result-list">${catalog.highlightedIds
          .map((entryId) => catalog.byId.get(entryId))
          .filter((entry): entry is ReferenceEntry => entry !== undefined)
          .map((entry) => renderResultButton(entry, state))
          .join("")}</div>
      </section>
    `;
  }

  const entries = visibleEntries(catalog, state);
  if (entries.length === 0) {
    return `
      <section class="reference-empty-state">
        <h3>No Matches</h3>
        <p>Try another mnemonic, BASIC or FORTH word, shell command, address, register name, or memory term.</p>
      </section>
    `;
  }

  const groups: Array<[ReferenceKind, ReferenceEntry[]]> = [];
  if (state.activeCategory === "all") {
    for (const kind of categoryOrder()) {
      const kindEntries = entries.filter((entry) => entry.kind === kind);
      if (kindEntries.length > 0) {
        groups.push([kind, kindEntries]);
      }
    }
  } else {
    groups.push([state.activeCategory, entries]);
  }

  return groups
    .map(
      ([kind, groupedEntries]) => `
        <section class="reference-overview-block">
          <div class="reference-section-heading">
            <h3>${escapeHtml(categoryLabel(kind))}</h3>
            <p>${groupedEntries.length} match${groupedEntries.length === 1 ? "" : "es"}</p>
          </div>
          <div class="reference-result-list">${groupedEntries.map((entry) => renderResultButton(entry, state)).join("")}</div>
        </section>
      `
    )
    .join("");
}

function renderRelated(catalog: ReferenceCatalog, entryIds: string[], selectedId: string | null): string {
  const filteredIds = entryIds.filter((entryId) => catalog.byId.has(entryId));
  if (filteredIds.length === 0) {
    return "<p class=\"reference-detail__empty\">No linked references.</p>";
  }
  return `<div class="reference-chip-row">${renderChipButtons(catalog, filteredIds, selectedId, "reference-link-chip")}</div>`;
}

function renderAssemblerGuideDetail(entry: AssemblerGuideEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  const examples =
    entry.examples.length === 0
      ? '<p class="reference-detail__empty">No examples documented for this entry.</p>'
      : `<div class="reference-chip-row reference-chip-row--static">${entry.examples
          .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
          .join("")}</div>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Assembler Guide</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Source</span><strong>${escapeHtml(entry.source)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Examples</h4>
      ${examples}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderBasicGuideDetail(entry: BasicGuideEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  const examples =
    entry.examples.length === 0
      ? '<p class="reference-detail__empty">No examples documented for this entry.</p>'
      : `<div class="reference-chip-row reference-chip-row--static">${entry.examples
          .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
          .join("")}</div>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">BASIC Guide</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Source</span><strong>${escapeHtml(entry.source)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Examples</h4>
      ${examples}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderDebuggerGuideDetail(entry: DebuggerGuideEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  const examples =
    entry.examples.length === 0
      ? '<p class="reference-detail__empty">No examples documented for this entry.</p>'
      : `<div class="reference-chip-row reference-chip-row--static">${entry.examples
          .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
          .join("")}</div>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Debugger Guide</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Source</span><strong>${escapeHtml(entry.source)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Examples</h4>
      ${examples}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderTrackerGuideDetail(entry: TrackerGuideEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  const examples =
    entry.examples.length === 0
      ? '<p class="reference-detail__empty">No examples documented for this tracker entry.</p>'
      : `<div class="reference-chip-row reference-chip-row--static">${entry.examples
          .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
          .join("")}</div>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Tracker Guide</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Source</span><strong>${escapeHtml(entry.source)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Examples</h4>
      ${examples}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderForthGuideDetail(entry: ForthGuideEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  const examples =
    entry.examples.length === 0
      ? '<p class="reference-detail__empty">No examples documented for this entry.</p>'
      : `<div class="reference-chip-row reference-chip-row--static">${entry.examples
          .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
          .join("")}</div>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">FORTH Guide</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Source</span><strong>${escapeHtml(entry.source)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Examples</h4>
      ${examples}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderShellCommandDetail(entry: ShellCommandEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  const examples =
    entry.examples.length === 0
      ? '<p class="reference-detail__empty">No examples documented for this command.</p>'
      : `<div class="reference-chip-row reference-chip-row--static">${entry.examples
          .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
          .join("")}</div>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Shell Command</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Availability</span><strong>${escapeHtml(entry.availability)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Examples</h4>
      ${examples}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderOpcodeDetail(entry: OpcodeEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Opcode</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-chip-row reference-chip-row--static">${entry.examples
      .map((example) => `<span class="reference-static-chip">${escapeHtml(example)}</span>`)
      .join("")}</div>
    <div class="reference-detail__section">
      <h4>Variants</h4>
      <div class="reference-table-wrap">
        <table class="reference-table">
          <thead>
            <tr><th>Opcode</th><th>Mode</th><th>Bytes</th><th>Cycles</th><th>Flags</th></tr>
          </thead>
          <tbody>
            ${entry.variants
              .map(
                (variant) => `
                  <tr>
                    <td>${escapeHtml(variant.opcode)}</td>
                    <td><button class="reference-inline-link" type="button" data-reference-action="open-entry" data-reference-entry-id="${escapeHtml(
                      `addressing-mode-${variant.modeId.replace(/_/g, "-")}`
                    )}" data-reference-category="addressing-mode" data-reference-clear-query="true">${escapeHtml(variant.modeLabel)}</button></td>
                    <td>${variant.bytes}</td>
                    <td>${escapeHtml(variant.cycles)}</td>
                    <td>${escapeHtml(variant.flagsText)}</td>
                  </tr>
                `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>
    <div class="reference-detail__section">
      <h4>Affected Flags</h4>
      ${entry.affectedFlags.length === 0 ? '<p class="reference-detail__empty">This family does not change flags directly.</p>' : renderRelated(catalog, entry.affectedFlags.map((flag) => `flag-${flag.toLowerCase()}`), selectedId)}
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderAddressingModeDetail(entry: AddressingModeEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Addressing Mode</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Syntax</span><strong>${escapeHtml(entry.syntax)}</strong></div>
      <div><span>Bytes</span><strong>${entry.bytes}</strong></div>
      <div><span>Operand</span><strong>${escapeHtml(entry.valueKind)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Effective Address</h4>
      <p>${escapeHtml(entry.effectiveAddress)}</p>
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Used By</h4>
      ${renderRelated(catalog, entry.usedByIds, selectedId)}
    </div>
  `;
}

function renderRegisterDetail(entry: RegisterEntry, catalog: ReferenceCatalog, selectedId: string | null, base: ReferenceNumberBase): string {
  const bitFieldMarkup =
    entry.bitFields.length === 0
      ? '<p class="reference-detail__empty">No documented bit breakdown for this register.</p>'
      : `
        <div class="reference-table-wrap">
          <table class="reference-table">
            <thead><tr><th>Field</th><th>Bits</th><th>Meaning</th></tr></thead>
            <tbody>${entry.bitFields
              .map(
                (field) => `
                  <tr>
                    <td>${escapeHtml(field.label)}</td>
                    <td>${escapeHtml(field.value)}</td>
                    <td>${escapeHtml(field.description)}</td>
                  </tr>
                `
              )
              .join("")}</tbody>
          </table>
        </div>
      `;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Register</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Scope</span><strong>${escapeHtml(entry.scope)}</strong></div>
      <div><span>Width</span><strong>${escapeHtml(entry.width)}</strong></div>
      <div><span>Access</span><strong>${escapeHtml(entry.access)}</strong></div>
      <div><span>Reset</span><strong>${escapeHtml(entry.reset)}</strong></div>
      ${
        entry.address === null
          ? ""
          : `<div><span>Address</span><strong>${escapeHtml(formatAddressWithAlternate(entry.address, base))}</strong></div>`
      }
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Bit Layout</h4>
      ${bitFieldMarkup}
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderFlagDetail(entry: FlagEntry, catalog: ReferenceCatalog, selectedId: string | null): string {
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Flag</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Bit</span><strong>${escapeHtml(entry.bit)}</strong></div>
      <div><span>Register</span><strong>P</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Meaning</h4>
      <p>${escapeHtml(entry.meaning)}</p>
    </div>
    <div class="reference-detail__section">
      <h4>Set By</h4>
      <ul class="reference-note-list">${entry.setBy.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Cleared By</h4>
      <ul class="reference-note-list">${entry.clearedBy.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, [entry.registerId, ...entry.relatedIds], selectedId)}
    </div>
  `;
}

function renderMemoryRangeDetail(entry: MemoryRangeEntry, catalog: ReferenceCatalog, selectedId: string | null, base: ReferenceNumberBase): string {
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Memory Range</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Range</span><strong>${escapeHtml(formatRange(entry.start, entry.end, base))}</strong></div>
      <div><span>Owner</span><strong>${escapeHtml(entry.owner)}</strong></div>
      <div><span>Access</span><strong>${escapeHtml(entry.access)}</strong></div>
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderPublicApiDetail(entry: PublicApiEntry, catalog: ReferenceCatalog, selectedId: string | null, base: ReferenceNumberBase): string {
  const inputs =
    entry.inputs.length === 0
      ? '<p class="reference-detail__empty">No required inputs documented.</p>'
      : `<ul class="reference-note-list">${entry.inputs.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>`;
  const outputs =
    entry.outputs.length === 0
      ? '<p class="reference-detail__empty">No explicit outputs documented.</p>'
      : `<ul class="reference-note-list">${entry.outputs.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}</ul>`;
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Public API</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      <div><span>Scope</span><strong>${escapeHtml(entry.scope)}</strong></div>
      <div><span>Binding</span><strong>${escapeHtml(entry.binding)}</strong></div>
      ${
        entry.address === null
          ? ""
          : `<div><span>Address</span><strong>${escapeHtml(
              entry.addressLabel === null
                ? formatAddressWithAlternate(entry.address, base)
                : `${entry.addressLabel} | ${formatAddressWithAlternate(entry.address, base)}`
            )}</strong></div>`
      }
    </div>
    <div class="reference-detail__section">
      <h4>Inputs / Call Shape</h4>
      ${inputs}
    </div>
    <div class="reference-detail__section">
      <h4>Outputs / Effects</h4>
      ${outputs}
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderInterruptDetail(entry: InterruptEntry, catalog: ReferenceCatalog, selectedId: string | null, base: ReferenceNumberBase): string {
  return `
    <div class="reference-detail__header">
      <p class="reference-detail__kind">Interrupt</p>
      <h3>${escapeHtml(entry.title)}</h3>
      <p class="reference-detail__summary">${escapeHtml(entry.summary)}</p>
    </div>
    <div class="reference-detail__stats">
      ${entry.vectors
        .map(
          (vector) => `<div><span>${escapeHtml(vector.label)}</span><strong>${escapeHtml(formatAddressWithAlternate(vector.address, base))}</strong></div>`
        )
        .join("")}
    </div>
    <div class="reference-detail__section">
      <h4>CPU Response</h4>
      <p>${escapeHtml(entry.response)}</p>
    </div>
    <div class="reference-detail__section">
      <h4>Sources</h4>
      <ul class="reference-note-list">${entry.sources.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Acknowledge And Return</h4>
      <ul class="reference-note-list">${entry.acknowledge.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Notes</h4>
      <ul class="reference-note-list">${entry.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>
    </div>
    <div class="reference-detail__section">
      <h4>Related</h4>
      ${renderRelated(catalog, entry.relatedIds, selectedId)}
    </div>
  `;
}

function renderDetail(catalog: ReferenceCatalog, state: ReferenceState): string {
  const entry = state.selectedId === null ? undefined : catalog.byId.get(state.selectedId);
  if (entry === undefined) {
    return `
      <div class="reference-placeholder">
        <div class="reference-detail__header">
          <p class="reference-detail__kind">Reference Guide</p>
          <h3>Search the machine</h3>
          <p class="reference-detail__summary">Look up opcodes, tracker workflow, debugger DSL syntax, BASIC and FORTH forms, assembler directives, shell commands, public ROM APIs, interrupts, registers, memory ranges, and key machine addresses like <code>ADC</code>, <code>Play from</code>, <code>$E884 and z == 0</code>, <code>on write mem[$1234]</code>, <code>IF</code>, <code>DUP</code>, <code>DOES&gt;</code>, <code>LOAD</code>, <code>.screenz</code>, <code>API_GFX_CLEAR</code>, <code>AUD_PITCH_CFG</code>, <code>BOARD_CFG0</code>, <code>$D012</code>, or <code>Zero Page</code>.</p>
        </div>
        <div class="reference-chip-row">${renderChipButtons(catalog, catalog.commonLookupIds, null, "reference-link-chip")}</div>
        <div class="reference-detail__stats">
          <div><span>Assembler</span><strong>${catalog.counts["assembler-guide"]}</strong></div>
          <div><span>BASIC</span><strong>${catalog.counts["basic-guide"]}</strong></div>
          <div><span>Debugger</span><strong>${catalog.counts["debugger-guide"]}</strong></div>
          <div><span>Tracker</span><strong>${catalog.counts["tracker-guide"]}</strong></div>
          <div><span>FORTH</span><strong>${catalog.counts["forth-guide"]}</strong></div>
          <div><span>Shell</span><strong>${catalog.counts["shell-command"]}</strong></div>
          <div><span>Opcodes</span><strong>${catalog.counts.opcode}</strong></div>
          <div><span>Modes</span><strong>${catalog.counts["addressing-mode"]}</strong></div>
          <div><span>APIs</span><strong>${catalog.counts["public-api"]}</strong></div>
          <div><span>Interrupts</span><strong>${catalog.counts.interrupt}</strong></div>
          <div><span>Registers</span><strong>${catalog.counts.register}</strong></div>
          <div><span>Flags</span><strong>${catalog.counts.flag}</strong></div>
          <div><span>Memory</span><strong>${catalog.counts["memory-range"]}</strong></div>
        </div>
      </div>
    `;
  }
  switch (entry.kind) {
    case "assembler-guide":
      return renderAssemblerGuideDetail(entry, catalog, state.selectedId);
    case "basic-guide":
      return renderBasicGuideDetail(entry, catalog, state.selectedId);
    case "debugger-guide":
      return renderDebuggerGuideDetail(entry, catalog, state.selectedId);
    case "tracker-guide":
      return renderTrackerGuideDetail(entry, catalog, state.selectedId);
    case "forth-guide":
      return renderForthGuideDetail(entry, catalog, state.selectedId);
    case "shell-command":
      return renderShellCommandDetail(entry, catalog, state.selectedId);
    case "opcode":
      return renderOpcodeDetail(entry, catalog, state.selectedId);
    case "addressing-mode":
      return renderAddressingModeDetail(entry, catalog, state.selectedId);
    case "register":
      return renderRegisterDetail(entry, catalog, state.selectedId, state.numberBase);
    case "flag":
      return renderFlagDetail(entry, catalog, state.selectedId);
    case "interrupt":
      return renderInterruptDetail(entry, catalog, state.selectedId, state.numberBase);
    case "memory-range":
      return renderMemoryRangeDetail(entry, catalog, state.selectedId, state.numberBase);
    case "public-api":
      return renderPublicApiDetail(entry, catalog, state.selectedId, state.numberBase);
  }
}

function renderMatchCount(catalog: ReferenceCatalog, state: ReferenceState): string {
  if (isOverview(state)) {
    return `${catalog.entries.length} references`;
  }
  const matches = visibleEntries(catalog, state).length;
  return `${matches} match${matches === 1 ? "" : "es"}`;
}

function referenceKindForId(catalog: ReferenceCatalog, entryId: string): ReferenceKind | null {
  const entry = catalog.byId.get(entryId);
  return entry?.kind ?? null;
}

export function setupReferenceGuide(container: HTMLElement): void {
  const catalog = createReferenceCatalog();
  const selectedFromHash = parseReferenceHash(window.location.hash);
  const hashedEntry = selectedFromHash !== null ? catalog.byId.get(selectedFromHash) : undefined;
  const state: ReferenceState = {
    activeCategory: hashedEntry?.kind ?? "all",
    numberBase: "hex",
    query: "",
    recentIds: loadRecentIds(catalog),
    selectedId: hashedEntry?.id ?? null
  };

  container.innerHTML = `
    <section class="reference-guide">
      <div class="reference-toolbar">
        <label class="reference-search" for="reference-search">
          <span class="field-label">Search</span>
          <input id="reference-search" class="field-input" type="search" spellcheck="false" placeholder="Search opcodes, tracker workflow, BASIC/FORTH syntax, shell commands, directives, APIs, interrupts, addresses, registers...">
        </label>
        <div class="reference-toolbar__controls">
          <div class="reference-filter-group" role="group" aria-label="Reference filters">
            <button class="reference-pill is-active" type="button" data-reference-action="set-category" data-reference-category="all" aria-pressed="true">All</button>
            ${categoryOrder()
              .map(
                (kind) =>
                  `<button class="reference-pill" type="button" data-reference-action="set-category" data-reference-category="${kind}" aria-pressed="false">${escapeHtml(
                    kind === "memory-range" ? "Memory" : categoryLabel(kind)
                  )}</button>`
              )
              .join("")}
          </div>
          <div class="reference-filter-group" role="group" aria-label="Reference number base">
            <button class="reference-pill is-active" type="button" data-reference-action="set-base" data-reference-base="hex" aria-pressed="true">Hex</button>
            <button class="reference-pill" type="button" data-reference-action="set-base" data-reference-base="dec" aria-pressed="false">Dec</button>
          </div>
        </div>
        <p id="reference-count" class="reference-count">${renderMatchCount(catalog, state)}</p>
      </div>
      <div class="reference-layout">
        <aside id="reference-sidebar" class="reference-sidebar"></aside>
        <section id="reference-results" class="reference-results"></section>
        <section id="reference-detail" class="reference-detail"></section>
      </div>
    </section>
  `;

  const searchInput = container.querySelector<HTMLInputElement>("#reference-search");
  const countNode = container.querySelector<HTMLElement>("#reference-count");
  const sidebarNode = container.querySelector<HTMLElement>("#reference-sidebar");
  const resultsNode = container.querySelector<HTMLElement>("#reference-results");
  const detailNode = container.querySelector<HTMLElement>("#reference-detail");
  if (searchInput === null || countNode === null || sidebarNode === null || resultsNode === null || detailNode === null) {
    throw new Error("reference guide shell failed to initialize");
  }

  const render = (): void => {
    sidebarNode.innerHTML = renderSidebar(catalog, state);
    resultsNode.innerHTML = renderResults(catalog, state);
    detailNode.innerHTML = renderDetail(catalog, state);
    countNode.textContent = renderMatchCount(catalog, state);
    searchInput.value = state.query;

    container.querySelectorAll<HTMLButtonElement>("[data-reference-action='set-category']").forEach((button) => {
      const category = button.dataset.referenceCategory as ReferenceFilter | undefined;
      const active = category === state.activeCategory;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
    container.querySelectorAll<HTMLButtonElement>("[data-reference-action='set-base']").forEach((button) => {
      const base = button.dataset.referenceBase as ReferenceNumberBase | undefined;
      const active = base === state.numberBase;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });
  };

  const applyHashSelection = (): void => {
    const hashEntryId = parseReferenceHash(window.location.hash);
    if (hashEntryId === null || !catalog.byId.has(hashEntryId)) {
      return;
    }
    const entry = catalog.byId.get(hashEntryId);
    if (entry === undefined) {
      return;
    }
    state.query = "";
    state.activeCategory = entry.kind;
    state.selectedId = hashEntryId;
    render();
  };

  syncSelection(catalog, state);
  render();
  window.addEventListener("hashchange", () => {
    applyHashSelection();
  });

  searchInput.addEventListener("input", () => {
    state.query = searchInput.value;
    syncSelection(catalog, state);
    updateLocationHash(state.selectedId);
    render();
  });

  container.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const button = target.closest<HTMLButtonElement>("[data-reference-action]");
    if (button === null) {
      return;
    }
    const action = button.dataset.referenceAction;
    if (action === "set-category") {
      const category = button.dataset.referenceCategory as ReferenceFilter | undefined;
      if (category === undefined) {
        return;
      }
      state.activeCategory = category;
      syncSelection(catalog, state);
      updateLocationHash(state.selectedId);
      render();
      return;
    }
    if (action === "set-base") {
      const base = button.dataset.referenceBase as ReferenceNumberBase | undefined;
      if (base === undefined) {
        return;
      }
      state.numberBase = base;
      render();
      return;
    }
    if (action === "open-entry") {
      const entryId = button.dataset.referenceEntryId;
      if (entryId === undefined || !catalog.byId.has(entryId)) {
        return;
      }
      const selectedCategory = button.dataset.referenceCategory as ReferenceFilter | undefined;
      if (selectedCategory !== undefined) {
        state.activeCategory = selectedCategory;
      }
      if (button.dataset.referenceClearQuery === "true") {
        state.query = "";
      }
      state.selectedId = entryId;
      state.recentIds = pushRecentId(state.recentIds, entryId);
      saveRecentIds(state.recentIds);
      updateLocationHash(entryId);
      if (state.activeCategory === "all" && button.dataset.referenceClearQuery === "true") {
        const kind = referenceKindForId(catalog, entryId);
        if (kind !== null) {
          state.activeCategory = kind;
        }
      }
      render();
    }
  });
}
